'use client';

import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from '../lib/translation';
import * as m from 'framer-motion/m';

export const SUB_NAV = [
  { key: 'howItWorks', enPath: '/sub/how-it-works', ltPath: '/lt/sub/kaip-veikia' },
  { key: 'faq', enPath: '/sub/faq', ltPath: '/lt/sub/duk' },
  { key: 'services', enPath: '/sub/services', ltPath: '/lt/sub/paslaugos' },
  { key: 'support', enPath: '/sub/support', ltPath: '/lt/sub/pagalba' },
  { key: 'tutorials', enPath: '/sub/tutorials', ltPath: '/lt/sub/pamokos' },
  { key: 'terms', enPath: '/sub/terms-of-service', ltPath: '/lt/sub/naudojimo-taisykles' },
  { key: 'cookies', enPath: '/sub/cookies-info', ltPath: '/lt/sub/slapukai' },
  { key: 'domains', enPath: '/sub/domains', ltPath: '/lt/sub/domenai' },
  { key: 'hosting', enPath: '/sub/hosting', ltPath: '/lt/sub/hostingas' },
  { key: 'mobileResponsive', enPath: '/sub/mobile-responsive', ltPath: '/lt/sub/mobili-versija' },
  { key: 'developerTools', enPath: '/sub/developer-tools', ltPath: '/lt/sub/kurimo-irankiai' },
];

interface SubDocsLayoutProps {
  children: React.ReactNode;
}

const SubDocsLayout = ({ children }: SubDocsLayoutProps) => {
  const { t, lang } = useTranslation('common');
  const router = useRouter();
  const isLt = lang === 'lt';
  const prefix = router.asPath.startsWith('/lt/') ? '/lt' : router.asPath.startsWith('/us/') ? '/us' : '';

  const getSubPath = (item: (typeof SUB_NAV)[0]) => {
    const base = isLt ? item.ltPath : item.enPath;
    if (prefix === '/us') {
      return base.replace('/sub/', '/us/sub/').replace('/lt/sub/', '/us/sub/');
    }
    return base;
  };

  const currentPath = router.asPath.split('?')[0];

  return (
    <div className='flex min-h-[70vh]' style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <aside className='hidden lg:block w-56 flex-shrink-0 border-r border-gray-300 dark:border-gray-700/30 pt-24 pl-6 pr-4'>
        <nav className='sticky top-28 space-y-0.5'>
          <p className='text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3'>
            {isLt ? 'Skaityti' : 'Docs'}
          </p>
          {SUB_NAV.map((item) => {
            const href = getSubPath(item);
            const isActive = currentPath === href || currentPath === href.replace('/us', '');
            const label = t(`footer.${item.key}`);
            return (
              <Link
                key={item.key}
                href={href}
                className={`block py-2 px-3 -ml-3 rounded-lg text-sm transition-colors ${
                  isActive
                    ? 'text-gray-900 dark:text-white font-medium bg-gray-100/50 dark:bg-white/5'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                {label}
              </Link>
            );
          })}
        </nav>
      </aside>

      <m.article
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className='flex-1 min-w-0 pt-24 pb-16 px-4 sm:px-8 lg:px-12 max-w-3xl'
      >
        {children}
      </m.article>
    </div>
  );
};

export default SubDocsLayout;
